import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import API from "../../../services/rest/api";
import {
  isChiefCoachFlag,
  readChiefHcFromStorage,
} from "../utils/isChiefCoach";

type ChiefCoachState = {
  /** True only when profile says chief_hc === "Y". */
  isChiefCoach: boolean;
  /** False until token boot + profile resolve attempt finishes. */
  ready: boolean;
};

/**
 * Boots webinar route auth (token / coach id) and resolves whether the
 * signed-in coach is a chief HC. Non-chiefs may view / copy / share only.
 */
export function useChiefCoachAccess(): ChiefCoachState {
  const params = useParams<{
    health_coach_id?: string;
    token?: string;
  }>();
  const [isChiefCoach, setIsChiefCoach] = useState(readChiefHcFromStorage);
  const [ready, setReady] = useState(false);

  const resolve = useCallback(async () => {
    const token = params.token;
    const coachId = params.health_coach_id;
    if (token && token !== ":token") {
      localStorage.setItem("token", token);
    }
    if (coachId && coachId !== ":health_coach_id") {
      localStorage.setItem("health_coach_id", coachId);
    }

    // Prefer fresh profile so role is correct on direct iframe entry.
    try {
      const userInfo = await API.GetProfileDetails();
      if (String(userInfo?.code) === "1" && userInfo?.data) {
        localStorage.setItem("userInfo", JSON.stringify(userInfo.data));
        if (userInfo.data.health_coach_id) {
          localStorage.setItem(
            "health_coach_id",
            String(userInfo.data.health_coach_id),
          );
        }
        setIsChiefCoach(isChiefCoachFlag(userInfo.data.chief_hc));
        setReady(true);
        return;
      }
    } catch {
      // fall through to cached userInfo
    }

    setIsChiefCoach(readChiefHcFromStorage());
    setReady(true);
  }, [params.health_coach_id, params.token]);

  useEffect(() => {
    resolve();
  }, [resolve]);

  return { isChiefCoach, ready };
}
